import { SensorHistoricalEvent, HVACMonitorReading } from '../../types';
import {
  HVACAirhandlerPacket,
  HVACReturnPacket,
  HVACSupplyPacket,
  HVACCTPacket,
  HVACPeakLoadAnalysis,
} from '../../types/hvac/HVACAirhandlerPackets';

export class HVACPeakLoadAnalysisService {
  /**
   * Parse packet data from sensor readings
   * Format examples:
   * - [DATA|0|21|R;26.15,73.53,-0.010]
   * - [DATA|0|23|S;15,98,60.25,0.320,505]
   */
  private static parsePacketData(
    event: SensorHistoricalEvent<HVACMonitorReading>,
  ): HVACAirhandlerPacket | null {
    try {
      const reading = event.values;
      if (!reading) {
        return null;
      }

      const timestamp = new Date(event.timestamp).getTime();
      const sensorId = event.sensorId;
      const hubId = event.hubId;

      if (reading.ct1 !== undefined || reading.ct2 !== undefined) {
        return {
          type: 'CT',
          timestamp,
          sensorId,
          hubId,
          ct1: reading.ct1 || 0,
          ct2: reading.ct2 || 0,
        } as HVACCTPacket;
      }

      const rawData = (reading as any).rawData || (reading as any).packetData;

      if (!rawData || typeof rawData !== 'string') {
        return null;
      }

      const packetMatch = rawData.match(/\[DATA\|\d+\|\d+\|([RS]);([^\]]+)\]/);

      if (!packetMatch) {
        return null;
      }

      const packetType = packetMatch[1] as 'R' | 'S';
      const values = packetMatch[2].split(',').map((v) => parseFloat(v.trim()));

      const basePacket = {
        timestamp,
        sensorId,
        hubId,
      };

      if (packetType === 'R' && values.length >= 3) {
        return {
          ...basePacket,
          type: 'R',
          temperature: values[0],
          humidity: values[1],
          pressure: values[2],
        } as HVACReturnPacket;
      } else if (packetType === 'S' && values.length >= 3) {
        return {
          ...basePacket,
          type: 'S',
          temperature: values[0],
          humidity: values[1],
          pressure: values[2],
          flow: values[3] || 0,
          other: values[4],
        } as HVACSupplyPacket;
      }

      return null;
    } catch (error) {
      console.warn('Failed to parse HVAC packet data:', error);
      return null;
    }
  }

  private static findPeakCTTimestamp(
    historicalData: SensorHistoricalEvent<HVACMonitorReading>[],
  ): { timestamp: number; ctValue: number } | null {
    let maxCTValue = 0;
    let peakTimestamp = 0;

    for (const event of historicalData) {
      const reading = event.values;
      if (!reading) continue;

      const totalCT = (reading.ct1 || 0) + (reading.ct2 || 0);

      if (totalCT > maxCTValue) {
        maxCTValue = totalCT;
        peakTimestamp = new Date(event.timestamp).getTime();
      }
    }

    return maxCTValue > 0 ? { timestamp: peakTimestamp, ctValue: maxCTValue } : null;
  }

  private static findClosestPackets(
    historicalData: SensorHistoricalEvent<HVACMonitorReading>[],
    targetTimestamp: number,
    timeWindowMs: number = 5 * 60 * 1000,
  ): { returnPacket: HVACReturnPacket | null; supplyPacket: HVACSupplyPacket | null } {
    let closestReturn: HVACReturnPacket | null = null;
    let closestSupply: HVACSupplyPacket | null = null;
    let minReturnDiff = Infinity;
    let minSupplyDiff = Infinity;

    for (const event of historicalData) {
      const packet = this.parsePacketData(event);

      if (!packet) continue;

      const timeDiff = Math.abs(packet.timestamp - targetTimestamp);

      if (timeDiff > timeWindowMs) continue;

      if (packet.type === 'R' && timeDiff < minReturnDiff) {
        closestReturn = packet as HVACReturnPacket;
        minReturnDiff = timeDiff;
      } else if (packet.type === 'S' && timeDiff < minSupplyDiff) {
        closestSupply = packet as HVACSupplyPacket;
        minSupplyDiff = timeDiff;
      }
    }

    return { returnPacket: closestReturn, supplyPacket: closestSupply };
  }

  private static calculatePressureDelta(
    returnPacket: HVACReturnPacket | null,
    supplyPacket: HVACSupplyPacket | null,
  ): number | null {
    if (!returnPacket || !supplyPacket) {
      return null;
    }

    return returnPacket.pressure - supplyPacket.pressure;
  }

  private static pressureDeltaToPercentage(pressureDelta: number): number {
    const minPressureDrop = 0.1;
    const maxPressureDrop = 1.0;

    const absPressureDelta = Math.abs(pressureDelta);
    let percentage =
      ((absPressureDelta - minPressureDrop) / (maxPressureDrop - minPressureDrop)) * 100;

    percentage = Math.max(0, Math.min(100, percentage));

    return percentage;
  }

  static async analyzeHVACPeakLoad(
    historicalData: SensorHistoricalEvent<HVACMonitorReading>[],
  ): Promise<HVACPeakLoadAnalysis | null> {
    if (!historicalData || historicalData.length === 0) {
      return null;
    }

    try {
      const peakCT = this.findPeakCTTimestamp(historicalData);

      if (!peakCT) {
        console.warn('No peak CT value found in historical data');
        return null;
      }

      const { returnPacket, supplyPacket } = this.findClosestPackets(
        historicalData,
        peakCT.timestamp,
      );
      const pressureDelta = this.calculatePressureDelta(returnPacket, supplyPacket);

      let filterHealthPercentage: number | null = null;
      if (pressureDelta !== null) {
        filterHealthPercentage = this.pressureDeltaToPercentage(pressureDelta);
      }

      return {
        peakTimestamp: peakCT.timestamp,
        peakCTValue: peakCT.ctValue,
        returnPacket,
        supplyPacket,
        pressureDelta,
        filterHealthPercentage,
        analysisTimestamp: Date.now(),
      };
    } catch (error) {
      console.error('Error analyzing HVAC peak load:', error);
      return null;
    }
  }

  static getFallbackFromLatestData(latestReading: HVACMonitorReading | null): {
    pressureDelta: number | null;
    filterHealthPercentage: number | null;
  } {
    if (!latestReading) {
      return { pressureDelta: null, filterHealthPercentage: null };
    }

    if (latestReading.percentPressure !== undefined) {
      return {
        pressureDelta: null,
        filterHealthPercentage: latestReading.percentPressure,
      };
    }

    if (latestReading.pressure !== undefined) {
      const percentage = this.pressureDeltaToPercentage(latestReading.pressure);
      return {
        pressureDelta: latestReading.pressure,
        filterHealthPercentage: percentage,
      };
    }

    return { pressureDelta: null, filterHealthPercentage: null };
  }
}
